import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import type { LivePlayOutput } from '../../generated-api';

interface LivePlayCardProps {
  livePlay: LivePlayOutput;
}

const LivePlayCard: React.FC<LivePlayCardProps> = ({ livePlay }) => {
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateElapsedTime = () => {
      if (!livePlay || !livePlay.startedAt) return;

      const startedAt = new Date(livePlay.startedAt);
      const now = new Date();
      const diffMs = now.getTime() - startedAt.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);

      if (diffHours > 0) {
        const remainingMinutes = diffMinutes % 60;
        setElapsedTime(`${diffHours}h ${remainingMinutes}m`);
      } else if (diffMinutes > 0) {
        const remainingSeconds = diffSeconds % 60;
        setElapsedTime(`${diffMinutes}m ${remainingSeconds}s`);
      } else {
        setElapsedTime(`${diffSeconds}s`);
      }
    };

    updateElapsedTime();
    timerIntervalRef.current = setInterval(() => {
      updateElapsedTime();
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [livePlay]);

  // Get all player names from teams
  const allPlayers = Object.values(livePlay.teamDetails).flat();
  const playerNames = allPlayers.map(p => p.playerName).join(', ');

  return (
    <Link
      data-track="feed_live_play_card"
      to={`/live-plays/${livePlay.id}`}
      className="block bg-gradient-to-r from-primary-50 to-green-50 rounded-lg shadow-md border-2 border-primary-200 hover:shadow-lg transition-all duration-200 hover:border-primary-400"
    >
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <ClockIcon className="h-5 w-5 text-primary-600 animate-pulse" />
              <span className="text-xs font-semibold text-primary-700 uppercase tracking-wide">
                Live Game
              </span>
            </div>
            <h3 className="text-xl font-bold text-heading mb-1">
              {livePlay.gameName}
            </h3>
            <p className="text-sm text-body mb-3">
              {playerNames}
            </p>
            <div className="flex items-center space-x-2 text-primary-700 font-semibold">
              <ClockIcon className="h-4 w-4" />
              <span>{elapsedTime} playing</span>
            </div>
          </div>
          {livePlay.gameImageUrl && (
            <img
              src={livePlay.gameImageUrl}
              alt={livePlay.gameName}
              className="h-20 w-20 object-contain rounded-lg ml-4"
            />
          )}
        </div>
      </div>
    </Link>
  );
};

export default LivePlayCard;
